import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {  ArrowUpRight, Filter, Grid, List, Search } from 'lucide-react';
import BackToTopButton from '../components/BackToTopButton';
import LiquidGlass, { GlassFilter } from '../components/ui/liquid-glass';
import { LampContainer } from '../components/ui/lamp';


const PROJECTS_DATA = {
  hero: {
    label: "PORTFOLIO.25",
    title: "PROJECTS",
    subtitle: "A selection of work that showcases my skills and creativity",
  },

  categories: ['ALL', 'FULL-STACK', 'MOBILE APP', 'E-COMMERCE', 'UX/UI DESIGN'],

  projects: [
    {
      id: 1,
      title: "BS PARFUMS",
      subtitle: "Luxury Perfume Webshop",
      category: "E-COMMERCE",
      description: "A luxurious interactive webshop for exclusive perfumes, built solo with HTML, CSS, JavaScript, Laravel and Livewire. Elegant dark aesthetic with smooth animations.",
      image: "https://images.unsplash.com/photo-1588514912908-8f5891714f8d?q=80&w=1169&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      year: "2024",
      tags: ["Laravel", "Livewire", "PHP", "JavaScript"],
      color: "from-purple-500 to-pink-600",
    },
    {
      id: 2,
      title: "APPIES LEGOBIB",
      subtitle: "LEGO Library for Hospitalised Children",
      category: "FULL-STACK",
      description: "A platform for a hospital LEGO lending library, children can reserve sets, donors can contribute. Built with Angular, .NET, NX monorepo and deployed on Azure via CI/CD.",
      image: "https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=800&h=600&fit=crop",
      year: "2025",
      tags: ["Angular", ".NET", "NX Monorepo", "Azure", "CI/CD"],
      color: "from-yellow-500 to-orange-500",
    },
    {
      id: 3,
      title: "MOTOCONNECT",
      subtitle: "Motorcycle Riders App",
      category: "MOBILE APP",
      description: "A mobile app concept where motorcycle riders can connect, track rides and manage group trips. Presented as a startup pitch with Figma prototype and full branding.",
      image: "https://images.unsplash.com/photo-1552306062-29a5560e1c31?q=80&w=2070&auto=format&fit=crop",
      year: "2024",
      tags: ["Figma", "UX/UI", "Startup", "Branding"],
      color: "from-orange-500 to-red-600",
    },
    {
      id: 4,
      title: "AMAZON REDESIGN",
      subtitle: "Component-Driven UI Redesign",
      category: "UX/UI DESIGN",
      description: "A Figma redesign of Amazon's interface using a fully component-driven approach. Focused on reusable design systems, clean hierarchy and modern aesthetics.",
      image: "https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=800&h=600&fit=crop",
      year: "2025",
      tags: ["Figma", "Component Design", "Design System"],
      color: "from-blue-500 to-cyan-500",
    },
    {
      id: 5,
      title: "AR RACING",
      subtitle: "Augmented Reality Car Racing",
      category: "MOBILE APP",
      description: "A Flutter and Unity app where you race miniature AR cars in the real world using your camera. Cross-platform mobile with augmented reality and real-time multiplayer.",
      image: "https://images.unsplash.com/photo-1724908267988-63310819325c?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      year: "2025",
      tags: ["Flutter", "Unity", "AR", "Mobile"],
      color: "from-purple-500 to-pink-600",
    },
    {
      id: 6,
      title: "FRIDGECHEF",
      subtitle: "Smart Fridge & Recipe Manager",
      category: "FULL-STACK",
      description: "An app that helps you manage your fridge contents and suggests recipes based on what you have. Scan ingredients, track expiry dates and discover meals you can make right now.",
      image: "https://images.unsplash.com/photo-1606859191214-25806e8e2423?q=80&w=1135&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      year: "2025",
      tags: ["React", "Node.js", "API", "Mobile"],
      color: "from-green-500 to-teal-500",
    },
    {
      id: 7,
      title: "DUFFALO FC",
      subtitle: "Football Club Website",
      category: "FULL-STACK",
      description: "Full-stack website for a fictional football club with match info, player statistics and CRUD management. Built with Laravel, Livewire and Tailwind CSS.",
      image: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&h=600&fit=crop",
      year: "2025",
      tags: ["Laravel", "Livewire", "Tailwind", "CRUD"],
      color: "from-green-500 to-emerald-600",
    },
    {
      id: 9,
      title: "WISHMAP",
      subtitle: "Encrypted Birthday Wishlist App",
      category: "FULL-STACK",
      description: "A birthday wishlist web app where guests claim gifts via an interactive SVG mind map. All claims are AES-256-GCM encrypted, the birthday person stays completely surprised.",
      image: "https://images.unsplash.com/photo-1516799946559-464579a81a7a?q=80&w=1113&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      year: "2024",
      tags: ["Vue 3", "Web Crypto API", "SVG"],
      color: "from-yellow-400 to-amber-500",
    },
  ],

  ticker: ["PROJECTS", "PORTFOLIO", "CREATIVE", "DEVELOPMENT"],
};

function ProjectCard({ project, index, viewMode }) {
  const isListView = viewMode === 'list';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ delay: index * 0.1 }}
      className={`group ${isListView ? 'col-span-full' : ''}`}
    >
      <Link to={`/projects/${project.id}`}>
        <LiquidGlass
          className={`relative overflow-hidden transition-all duration-500 ${
            isListView
              ? 'rounded-[32px] h-[200px]'
              : 'rounded-[40px] h-[500px] lg:h-[600px]'
          }`}
        >
          <div className={`relative overflow-hidden transition-all duration-500 w-full h-full ${
            isListView ? 'flex' : ''
          }`}>
            {/* Background Image */}
            <div className={`${isListView ? 'w-1/3 h-full' : 'absolute inset-0'}`}>
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              {/* Gradient Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-${isListView ? 'r' : 't'} from-[#161616] via-[#161616]/80 to-transparent`} />
            </div>

            {/* Glassmorphic Background for List View */}
            {isListView && (
              <div className="flex-1 bg-[#151515] border border-white/5 group-hover:border-white/10 transition-all" />
            )}

          {/* Content Overlay */}
          <div className={`${
            isListView 
              ? 'flex-1 p-8 flex items-center justify-between bg-[#151515]' 
              : 'absolute inset-0 p-8 lg:p-10 flex flex-col justify-between'
          }`}>
            {/* Top Section */}
            {!isListView && (
              <div className="flex justify-between items-start">
                {/* Year Badge */}
                <div className="bg-white/10 backdrop-blur-xl px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white text-xs font-bold font-silkscreen">{project.year}</span>
                </div>

                {/* External Link Icon */}
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  whileHover={{ scale: 1.1 }}
                  className="bg-white/10 backdrop-blur-xl p-3 rounded-full border border-white/20 opacity-0 group-hover:opacity-100 transition-all duration-300"
                >
                  <ArrowUpRight className="w-5 h-5 text-white" />
                </motion.div>
              </div>
            )}

            {/* Bottom/Main Content */}
            <div className={`${isListView ? 'flex items-center justify-between w-full' : ''}`}>
              <div className={`${isListView ? '' : 'transform group-hover:-translate-y-2 transition-transform duration-300'}`}>
                {/* Category */}
                <span className="text-orange-500 text-xs tracking-widest font-silkscreen mb-2 block">
                  {project.category}
                </span>

                {/* Title */}
                <h3 className={`text-white font-silkscreen font-normal mb-2 ${
                  isListView ? 'text-2xl' : 'text-3xl lg:text-4xl'
                }`}>
                  {project.title}
                </h3>

                {/* Subtitle */}
                <p className="text-white/60 text-sm mb-3">
                  {project.subtitle}
                </p>

                {/* Description - Hidden on list view */}
                {!isListView && (
                  <p className="text-white/50 text-sm leading-relaxed max-h-0 group-hover:max-h-28 group-hover:mb-6 overflow-hidden opacity-0 group-hover:opacity-100 transition-all duration-300 max-w-md">
                    {project.description}
                  </p>
                )}

                {/* Tags */}
                <div className={`flex flex-wrap gap-2 ${isListView ? '' : 'max-h-0 group-hover:max-h-20 overflow-hidden opacity-0 group-hover:opacity-100 transition-all duration-300'}`}>
                  {project.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="text-xs bg-white/10 backdrop-blur-xl text-white/80 px-3 py-1 rounded-full border border-white/10"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* List View - Right Side */}
              {isListView && (
                <div className="flex items-center gap-4">
                  <span className="text-white/40 text-sm font-silkscreen">{project.year}</span>
                  <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ArrowUpRight className="w-5 h-5 text-white" />
                  </div>
                </div>
              )}
            </div>
          </div>

            {/* Hover Border Effect */}
            <div className="absolute inset-0 border-2 border-orange-500 rounded-[40px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

            {/* Colored Glow on Hover */}
            <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none`} />
          </div>
        </LiquidGlass>
      </Link>
    </motion.div>
  );
}

// MAIN COMPONENT

function ProjectsPage() {
  const [filter, setFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Filter projects
  const filteredProjects = PROJECTS_DATA.projects.filter(project => {
    const matchesCategory = filter === 'ALL' || project.category === filter;
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          project.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-[#0a0a0a] text-white min-h-screen"
    >
      {/* SVG Filter for Liquid Glass Effect */}
      <GlassFilter />

      <LampContainer>
        <motion.span
          initial={{ opacity: 0.5, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.3,
            duration: 0.8,
            ease: "easeInOut",
          }}
          className="text-orange-500 text-xs sm:text-sm tracking-widest mb-6 block font-silkscreen"
        >
          {PROJECTS_DATA.hero.label}
        </motion.span>

        <motion.h1
          initial={{ opacity: 0.5, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.3,
            duration: 0.8,
            ease: "easeInOut",
          }}
          className="mt-8 bg-gradient-to-br from-slate-300 to-slate-500 py-4 bg-clip-text text-center text-4xl 
          font-medium tracking-tight text-[#F97316] md:text-7xl font-silkscreen"
        >
          {PROJECTS_DATA.hero.title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0.5, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.4,
            duration: 0.8,
            ease: "easeInOut",
          }}
          className="text-white/60 text-sm sm:text-base max-w-2xl mx-auto mt-4 text-center leading-relaxed"
        >
          {PROJECTS_DATA.hero.subtitle}
        </motion.p>
      </LampContainer>

      {/* Stats Row */}
      <section className="mt-8 sm:mt-12 py-4 sm:py-6 px-4 sm:px-6 lg:px-12 bg-[#0a0a0a] relative z-10">
        <div className="max-w-[1800px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="grid grid-cols-3 gap-3 sm:gap-4 max-w-3xl mx-auto"
          >
            {[
              { number: PROJECTS_DATA.projects.length, label: "TOTAL PROJECTS" },
              { number: "4", label: "CATEGORIES" },
              { number: "2026", label: "LATEST YEAR" },
            ].map((stat, idx) => (
              <div
                key={idx}
                className="bg-[#151515] rounded-xl sm:rounded-2xl p-3 sm:p-6 text-center border border-white/5"
              >
                <div className="text-2xl sm:text-3xl lg:text-4xl text-orange-500 font-silkscreen">
                  {stat.number}
                </div>
                <div className="text-white/40 text-[9px] sm:text-xs tracking-widest mt-1 font-silkscreen">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>


      <section className="top-20 z-40 py-4 sm:py-6 px-4 sm:px-6 lg:px-12 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex flex-col gap-4 sm:gap-6">
            {/* Filter Tabs */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex flex-wrap gap-2 justify-center lg:justify-start"
            >
              {PROJECTS_DATA.categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-full text-xs sm:text-sm font-bold transition-all font-silkscreen ${
                    filter === cat
                      ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </motion.div>

            {/* Search & View Controls */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3 sm:gap-4 justify-between"
            >
              {/* Search Input */}
              <div className="relative flex-1 max-w-xs sm:max-w-md">
                <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-full pl-10 sm:pl-11 pr-4 sm:pr-5 py-2 sm:py-2.5 text-xs sm:text-sm text-white placeholder-white/40 focus:outline-none focus:border-orange-500 transition-colors w-full"
                />
              </div>

              {/* View Mode Toggle */}
              <div className="flex bg-white/5 rounded-full p-1 border border-white/10 flex-shrink-0">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 sm:p-2.5 rounded-full transition-all ${
                    viewMode === 'grid' ? 'bg-orange-500 text-white' : 'text-white/40 hover:text-white'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 sm:p-2.5 rounded-full transition-all ${
                    viewMode === 'list' ? 'bg-orange-500 text-white' : 'text-white/40 hover:text-white'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-12 overflow-hidden">
        <div className="max-w-[1800px] mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              layout
              className={`grid gap-6 lg:gap-8 ${
                viewMode === 'grid' ? 'md:grid-cols-2' : 'grid-cols-1'
              }`}
            >
              {filteredProjects.map((project, idx) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  index={idx}
                  viewMode={viewMode}
                />
              ))}
            </motion.div>
          </AnimatePresence>

          {/* No Results */}
          {filteredProjects.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-24"
            >
              <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
                <Filter className="w-8 h-8 text-white/20" />
              </div>
              <h3 className="text-2xl font-bold text-white/40 mb-2 font-silkscreen">
                NO PROJECTS FOUND
              </h3>
              <p className="text-white/30">
                Try adjusting your filters or search query
              </p>
              <button
                onClick={() => { setFilter('ALL'); setSearchQuery(''); }}
                className="mt-6 bg-orange-500 text-white px-6 py-3 rounded-full font-bold font-silkscreen text-sm hover:bg-orange-600 transition-colors"
              >
                CLEAR FILTERS
              </button>
            </motion.div>
          )}
        </div>
      </section>

      <section className="py-24 lg:py-32 px-6 lg:px-12 bg-[#0a0a0a]">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative bg-[#0a0a0a] border border-white/10 rounded-[40px] overflow-hidden"
          >
            {/* Subtle gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-purple-500/5" />

            {/* Content */}
            <div className="relative z-10 p-12 lg:p-16 text-center">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-orange-500 text-xs sm:text-sm tracking-widest mb-6 block font-silkscreen"
              >
                READY TO COLLABORATE?
              </motion.span>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-4xl lg:text-5xl font-bold text-white mb-6 font-silkscreen leading-tight"
              >
                Let's Build Something<br />
                <span className="bg-gradient-to-r from-orange-500 to-white bg-clip-text text-transparent">
                  Extraordinary
                </span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-white/60 text-sm sm:text-base max-w-2xl mx-auto mb-10 leading-relaxed"
              >
                Whether you have a clear vision or just an idea, I'm here to help bring it to life with modern, scalable solutions.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="group flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-bold transition-all font-silkscreen shadow-lg shadow-orange-500/20"
                  >
                    GET IN TOUCH
                    <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform" />
                  </motion.button>
                </Link>
                <a href="mailto:bekirhan-simsek@hotmail.com">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="group flex items-center justify-center gap-3 bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-full font-bold transition-all border border-white/10 hover:border-white/20 font-silkscreen"
                  >
                    SEND EMAIL
                    <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform" />
                  </motion.button>
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative py-8 sm:py-12 overflow-hidden bg-gradient-to-b from-[#0a0a0a] to-black">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.03, 0.06, 0.03],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-20 left-1/4 w-96 h-96 bg-orange-500 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.02, 0.05, 0.02],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute -bottom-20 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl"
          />
        </div>

        <div className="relative">
          {/* Top line decoration */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent mb-6 sm:mb-8"
          />

          {/* Main marquee */}
          <div className="overflow-hidden">
            <motion.div
              animate={{ x: ["-50%", "0%"] }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              className="flex items-center gap-12 sm:gap-16 whitespace-nowrap"
            >
              {[...Array(10)].map((_, i) => (
                <React.Fragment key={i}>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <motion.span
                      className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-silkscreen bg-gradient-to-r from-white via-orange-500 to-white bg-clip-text text-transparent font-normal"
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: "spring", stiffness: 400 }}
                      style={{
                        backgroundSize: '200% auto',
                      }}
                    >
                      BEKIRHAN SIMSEK
                    </motion.span>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                      className="w-2 h-2 sm:w-3 sm:h-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full shadow-lg shadow-orange-500/50"
                    />
                  </div>
                </React.Fragment>
              ))}
            </motion.div>
          </div>

          {/* Bottom line decoration */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
            className="h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent mt-6 sm:mt-8"
          />
        </div>
      </section>

      {/* Back to Top Button with Sticky Label */}
      <BackToTopButton />
    </motion.div>
  );
}

export default ProjectsPage;